import { useEffect, useRef } from "react";
import { useZero } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import { mutators } from "@/lib/mutators";
import { generateSlugFromName } from "@/lib/workspace-validation";
import {
  peekPendingDraftReply,
  takePendingDraftReply,
  type PendingDraftReply,
} from "@/lib/pending-draft-reply";

// The new member's `channel_members` row is written server-side by the
// auto-join flow, then replicated back via Zero. Posting before that row is
// visible would bounce on the `messages.insert` / `addReply` membership gate
// (Pattern 6), so we poll for the row to appear before publishing.
const MEMBERSHIP_POLL_INTERVAL_MS = 500;
const MEMBERSHIP_POLL_TIMEOUT_MS = 15_000;

/** How the membership poll resolved. `timeout` keeps the draft for a retry. */
type PublishOutcome = "published" | "timeout" | "error" | "no-draft";

interface MembershipRow {
  channel_id: string;
  user_id: string;
}

interface WorkspaceRow {
  id: string;
  slug: string;
  name: string | null;
}

type ZeroLike = {
  run: <T>(query: unknown) => Promise<T>;
  mutate: (mutation: unknown) => unknown;
};

/**
 * After a logged-out visitor stashed a reply draft on a public-workspace page
 * and signed up via "Continue with GitHub", restore + publish that draft once
 * — as soon as their new channel membership is visible to Zero.
 *
 * Mounted in `AppInner` AFTER `useAutoJoinAfterAuth`, so by the time `userId`
 * is set the auto-join has been kicked off (its `channel_members` row is on its
 * way through Zero replication). This hook GATES the post on that row appearing
 * (polls up to ~15s), then publishes via the mutators registry:
 *   - `messages.addReply(...)` when the draft has a `targetMessageId` (answering
 *     a specific question → a thread reply).
 *   - `messages.insert(...)`   for a plain top-level channel reply.
 *
 * On success it navigates to the channel (dispatching the same
 * `ano:open-channel-from-shell` event `useShellDeepLinkListener` already
 * handles → `openChannelWithContext`), and the one-shot `take` consumes the
 * draft. On timeout the draft is intentionally KEPT (not consumed) so a later
 * mount / reconnect can still publish it; on a publish error the draft is
 * consumed (it was a real attempt) and the failure is logged.
 *
 * Double-fire is guarded by a per-mount ref plus the one-shot `take`.
 */
export function usePendingDraftReply(userId: string | null | undefined): void {
  const zero = useZero() as unknown as ZeroLike;
  // Per-mount guard: the restore runs at most once even if the effect re-runs
  // (Zero client identity change, Strict-Mode double-invoke). A remount can't
  // re-fire after success either — `take` consumed the one-shot draft.
  const firedRef = useRef(false);

  useEffect(() => {
    if (!userId || firedRef.current) return;
    // Peek (not take) — we only consume the draft once we're committed to a
    // publish attempt OR the poll times out. Bailing here with the draft
    // intact lets a later mount retry.
    const draft = peekPendingDraftReply();
    if (!draft) return;
    firedRef.current = true;

    let cancelled = false;
    void restoreAndPublish(zero, userId, draft, () => cancelled)
      .then((outcome) => {
        // On a membership-poll timeout the draft is KEPT (not consumed); release
        // the per-mount latch so a later effect re-run (Zero identity change /
        // reconnect) can retry the publish within the draft's TTL.
        if (outcome === "timeout") firedRef.current = false;
      })
      .catch((err) => {
        console.warn("[pending-draft-reply] restore/publish failed", err);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, zero]);
}

/**
 * Poll for the caller's channel membership, then publish the draft. Exported
 * for unit tests. `isCancelled` lets the caller abort on unmount so a slow
 * poll doesn't publish into an unmounted tree.
 */
export async function restoreAndPublish(
  zero: ZeroLike,
  userId: string,
  draft: PendingDraftReply,
  isCancelled: () => boolean,
): Promise<PublishOutcome> {
  // Two gate shapes:
  //  - Channel reply (channelId present) → poll the precise channel membership.
  //  - Anchored answer from the question surface (channelId="" — that surface
  //    has no channel_id and relies on `addReply` deriving the channel from the
  //    parent message) → poll the WORKSPACE membership instead, which confirms
  //    the auto-join landed. `addReply` re-checks channel membership
  //    server-side, so this is a sufficient pre-publish gate.
  const membershipVisible = draft.channelId
    ? await waitForChannelMembership(zero, draft.channelId, userId, isCancelled)
    : await waitForWorkspaceMembership(zero, draft.slug, userId, isCancelled);
  if (isCancelled()) return "error";
  if (!membershipVisible) {
    // KEEP the draft — a reconnect / later mount can still publish it. Do NOT
    // take().
    return "timeout";
  }

  // Commit to the attempt: consume the one-shot draft so a reload can't
  // double-publish. (`take` also clears it on the malformed/expired path.)
  const taken = takePendingDraftReply();
  if (!taken) return "no-draft"; // raced with another consumer — nothing to do.

  try {
    await publishDraft(zero, userId, taken);
  } catch (err) {
    // The draft is already consumed (a real attempt was made). Surface a
    // non-blocking notice and let the caller log.
    notifyPublishFailure();
    throw err;
  }
  navigateToChannel(taken);
  return "published";
}

/** Poll `channel_members.byChannelAndUser` until the row appears or we time out. */
async function waitForChannelMembership(
  zero: ZeroLike,
  channelId: string,
  userId: string,
  isCancelled: () => boolean,
): Promise<boolean> {
  const deadline = Date.now() + MEMBERSHIP_POLL_TIMEOUT_MS;
  while (!isCancelled()) {
    try {
      const row = await zero.run<MembershipRow | null | undefined>(
        queries.channel_members.byChannelAndUser({ channelId, userId }),
      );
      if (row) return true;
    } catch {
      // Transient query failure (sync not ready) — fall through to retry.
    }
    if (Date.now() >= deadline) return false;
    await delay(MEMBERSHIP_POLL_INTERVAL_MS);
  }
  return false;
}

/**
 * Poll the caller's ACTIVE workspace memberships until the target slug appears
 * — the gate for anchored answers (empty `channelId`). `workspaces.byUser`
 * already filters `removed_at IS NULL` on the caller's membership, so a row
 * here means the auto-join landed.
 */
async function waitForWorkspaceMembership(
  zero: ZeroLike,
  slug: string,
  userId: string,
  isCancelled: () => boolean,
): Promise<boolean> {
  void userId; // auth is implicit via the query's `ctx.sub`; kept for symmetry.
  const deadline = Date.now() + MEMBERSHIP_POLL_TIMEOUT_MS;
  while (!isCancelled()) {
    try {
      const rows = await zero.run<WorkspaceRow[] | null | undefined>(
        queries.workspaces.byUser(),
      );
      const matched = (rows ?? []).some(
        (w) => w.slug === slug || generateSlugFromName(w.name ?? "") === slug,
      );
      if (matched) return true;
    } catch {
      // Transient query failure (sync not ready) — fall through to retry.
    }
    if (Date.now() >= deadline) return false;
    await delay(MEMBERSHIP_POLL_INTERVAL_MS);
  }
  return false;
}

/** Publish the draft via the mutators registry — addReply vs insert by target. */
async function publishDraft(
  zero: ZeroLike,
  userId: string,
  draft: PendingDraftReply,
): Promise<void> {
  const now = Date.now();
  if (draft.targetMessageId) {
    // Answering a specific question → a thread reply off that message.
    await zero.mutate(
      mutators.messages.addReply({
        parent_id: draft.targetMessageId,
        reply: {
          id: crypto.randomUUID(),
          user_id: userId,
          content: draft.content,
          kind: "text",
          created_at: now,
          updated_at: now,
        },
      }),
    );
    return;
  }
  // Plain top-level channel reply.
  await zero.mutate(
    mutators.messages.insert({
      id: crypto.randomUUID(),
      channel_id: draft.channelId,
      user_id: userId,
      content: draft.content,
      kind: "text",
      created_at: now,
      updated_at: now,
    }),
  );
}

/**
 * Navigate to the published reply's channel. Reuses the
 * `ano:open-channel-from-shell` window event that `useShellDeepLinkListener`
 * fans into `openChannelWithContext` — so this hook doesn't need to be wired
 * into AppInner's channel-open closure, keeping the App.tsx edit minimal.
 */
function navigateToChannel(draft: PendingDraftReply): void {
  try {
    window.dispatchEvent(
      new CustomEvent("ano:open-channel-from-shell", {
        detail: {
          channelId: draft.channelId,
          messageId: draft.targetMessageId ?? null,
        },
      }),
    );
  } catch {
    /* non-DOM env (tests) — navigation is best-effort. */
  }
}

/** Non-blocking inline notice on publish failure (no Toaster dependency). */
function notifyPublishFailure(): void {
  try {
    window.dispatchEvent(
      new CustomEvent("ano:pending-draft-reply-failed", {
        detail: { reason: "publish-error" },
      }),
    );
  } catch {
    /* non-DOM env — nothing to notify. */
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
